import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { marked } from "marked";
import { getMessageText } from "./ai-message-utils";
import { transcriptSpeakerLabel } from "./transcript/is-rep-line";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function normalizeSimulationType(type) {
  if (!type) return "";
  return type
    .toString()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_")
    .toUpperCase();
}

// Helper function to extract YouTube video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;

  // Handle YouTube Shorts URLs
  if (url.includes("youtube.com/shorts/")) {
    const shortsId = url.split("youtube.com/shorts/")[1]?.split(/[?&]/)[0];
    return shortsId || null;
  }

  // Handle regular YouTube URLs
  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

/**
 * Render a chat thread as the persisted transcript string.
 *
 * @param {Array} data - Chat messages.
 * @param {string} [salesperson] - Prefix for the learner's turns.
 * @param {(message: Object, index: number) => (string|null|undefined)} [resolveSpeaker]
 *   Names the persona who spoke an assistant turn. Multi-persona sessions pass one;
 *   everything else omits it and the literal "Customer" prefix a dozen consumers
 *   still parse is written byte-for-byte as before.
 */
const conversationFromChat = (data, salesperson = "Salesperson", resolveSpeaker) => {
  if (!Array.isArray(data)) {
    return "";
  }

  return data
    .map((message, index) => {
      if (!message) {
        return "";
      }

      let role = message.role?.toLowerCase?.() || "assistant";
      // A resolved persona name is written verbatim; the fallback roles keep the
      // legacy first-letter capitalisation so nothing about them shifts.
      let resolvedLabel = "";
      if (role === "assistant") {
        resolvedLabel =
          typeof resolveSpeaker === "function"
            ? transcriptSpeakerLabel(resolveSpeaker(message, index))
            : "";
        role = "Customer";
      } else if (role === "user") {
        role = salesperson || "Salesperson";
      }

      const displayRole =
        resolvedLabel || role.charAt(0).toUpperCase() + role.slice(1);
      const text = getMessageText(message);

      return `${displayRole}: ${text}`;
    })
    .filter(Boolean)
    .join("\n");
};

const cleanScoreLabel = (label) =>
  label
    .replace(/[*_`]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const calculateScoreFromBreakdown = (breakdown) => {
  if (!Array.isArray(breakdown) || breakdown.length === 0) {
    return null;
  }

  const { scoreSum, maxSum } = breakdown.reduce(
    (totals, item) => {
      const score = Number(item?.score);
      const max = Number(item?.max);

      if (!Number.isFinite(score) || !Number.isFinite(max) || max <= 0) {
        return totals;
      }

      totals.scoreSum += score;
      totals.maxSum += max;
      return totals;
    },
    { scoreSum: 0, maxSum: 0 }
  );

  if (maxSum <= 0) {
    return null;
  }

  return Math.round((scoreSum / maxSum) * 100);
};

const extractCriteriaScoreBreakdown = (feedback) => {
  if (!feedback) return [];

  const breakdown = [];
  const seen = new Set();
  const lines = feedback.split("\n");
  let currentLabel = null;

  const criterionRegex =
    /^\s*(?:[-*•]|\d+\.)\s*(?:\*\*|__)?(.+?)(?:\*\*|__)?\s*:\s*$/;
  const ratingRegex =
    /^\s*(?:[-*•]|\d+\.)\s*(?:\*\*|__|\*|_)?Rating(?:\*\*|__|\*|_)?\s*:?\s*(\d{1,3})\s*\/\s*(\d{1,3})/i;

  for (const rawLine of lines) {
    const line = rawLine.trim();

    const criterionMatch = line.match(criterionRegex);
    if (criterionMatch) {
      currentLabel = cleanScoreLabel(criterionMatch[1]);
      continue;
    }

    const ratingMatch = line.match(ratingRegex);
    if (ratingMatch && currentLabel) {
      const score = Number(ratingMatch[1]);
      const max = Number(ratingMatch[2]);

      if (Number.isFinite(score) && Number.isFinite(max) && max > 0) {
        const key = `${currentLabel}-${max}`;
        if (!seen.has(key)) {
          seen.add(key);
          breakdown.push({ label: currentLabel, score, max });
        }
      }

      currentLabel = null;
    }
  }

  return breakdown;
};

const extractInlineScoreBreakdown = (feedback) => {
  if (!feedback) return [];

  const breakdown = [];
  const seen = new Set();
  const lineRegex =
    /(?:^|\n)\s*(?:[-*]\s*)?(?:\*\*|__)?([^:\n]{3,80}?)(?:\*\*|__)?\s*(?:-|–|:)?\s*(\d{1,3})\s*\/\s*(\d{1,3})/g;
  let match;

  while ((match = lineRegex.exec(feedback))) {
    const label = cleanScoreLabel(match[1]);
    const score = Number(match[2]);
    const max = Number(match[3]);
    const normalizedLabel = label.toLowerCase();

    if (!label || normalizedLabel === "score" || normalizedLabel === "rating") {
      continue;
    }

    if (!Number.isFinite(score) || !Number.isFinite(max) || max <= 0) {
      continue;
    }

    const key = `${label}-${max}`;
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    breakdown.push({ label, score, max });
  }

  return breakdown;
};

const parseRating = (feedback) => {
  if (!feedback || typeof feedback !== "string") {
    return null;
  }

  // Split the feedback into lines
  const lines = feedback.split("\n");

  // Find the index of the line that starts with "### Overall Score"
  const overallRatingIndex = lines.findIndex((line) =>
    line.trim().startsWith("### Overall Score")
  );

  // Look for the final score section at the end of the feedback
  const finalScoreIndex = lines.findIndex((line) =>
    line.trim().startsWith("- ***Score***:")
  );

  if (finalScoreIndex !== -1) {
    // Extract the score from the final score line
    const scoreLine = lines[finalScoreIndex].trim();
    const scoreMatch = scoreLine.match(
      /\*\*\*Score\*\*\*:\s*\[?(\d+\.?\d*)\/100\]?/
    );
    if (scoreMatch) {
      return Math.round(parseFloat(scoreMatch[1]));
    }
  }

  if (overallRatingIndex !== -1) {
    // Look for the Score line in the following lines
    for (let i = overallRatingIndex + 1; i < lines.length; i++) {
      const line = lines[i].trim();

      // Try all possible score formats in order of specificity

      // 1. Current format: - ***Score***: X/100
      const currentFormatMatch = line.match(
        /^\-\s+\*\*\*Score\*\*\*:\s*(\d+\.?\d*)\/100/
      );
      if (currentFormatMatch) {
        return Math.round(parseFloat(currentFormatMatch[1]));
      }

      // 2. Older formats with raw fractions
      const fractionMatch = line.match(/^(\d+\.?\d*)\/(\d+)$/);
      if (fractionMatch) {
        const [, numerator, denominator] = fractionMatch.map(Number);
        return Math.round((numerator / denominator) * 100);
      }

      // 3. [X/100] format
      const bracketMatch = line.match(/\[(\d+\.?\d*)\/100\]/);
      if (bracketMatch) {
        return Math.round(parseFloat(bracketMatch[1]));
      }

      // 4. "X out of Y" format
      const outOfMatch = line.match(/(\d+\.?\d*)\s+out\s+of\s+(\d+)/i);
      if (outOfMatch) {
        const [, numerator, denominator] = outOfMatch.map(Number);
        return Math.round((numerator / denominator) * 100);
      }

      // 5. Any other X/Y format in the line
      const anyFractionMatch = line.match(/(\d+\.?\d*)\/(\d+)/);
      if (anyFractionMatch) {
        const [, numerator, denominator] = anyFractionMatch.map(Number);
        return Math.round((numerator / denominator) * 100);
      }
    }
  }

  const criteriaBreakdown = extractCriteriaScoreBreakdown(feedback);
  const criteriaScore = calculateScoreFromBreakdown(criteriaBreakdown);
  if (typeof criteriaScore === "number") {
    return criteriaScore;
  }

  const inlineBreakdown = extractInlineScoreBreakdown(feedback);
  return calculateScoreFromBreakdown(inlineBreakdown);
};

const parseRatingCategory = (feedback, category) => {
  const regex = new RegExp(
    `${category}.*?\\(?(\\d+)\\/\\d+\\)?\\*?:?\\s*(\\d+)`
  );
  const match = feedback?.match(regex);
  return match ? parseInt(match[1] || match[2], 10) : null;
};

const calculateAverageScoreAllObjections = (results, category) => {
  return (
    Object.values(results).reduce((acc, result) => {
      return acc + result[category];
    }, 0) / Object.values(results).flatMap((result) => result.Scores).length
  );
};

const calculateAverageScoreSelectedObjection = (
  selectedObjection,
  category
) => {
  return (
    scores.reduce((acc, score) => {
      return acc + score[category];
    }, 0) / selectedObjection.Scores.length
  );
};

const getEncouragement = (score) => {
  if (score >= 90) return "Absolutely fantastic!";
  if (score >= 80) return "Excellent work!";
  if (score >= 70) return "Solid effort!";
  if (score >= 60)
    return "You're on the right track, but there's room for improvement!";
  if (score >= 50) return "You're halfway there, keep practicing!";
  if (score >= 40) return "Needs more work, but don't give up!";
  if (score >= 30) return "Significant improvement needed!";
  return "Don't worry, everyone starts somewhere—keep practicing!";
};

const getScoreColor100 = (score) => {
  if (score >= 80) return "bg-green-500"; // Green for scores 80+
  if (score >= 65) return "bg-yellow-400"; // Yellow for scores 65-79
  return "bg-red-500"; // Red for scores <65
};

const getScoreColor20 = (score) => {
  if (score >= 18) return "bg-green-500";
  if (score >= 16) return "bg-green-400";
  if (score >= 14) return "bg-green-300";
  if (score >= 12) return "bg-yellow-400";
  if (score >= 10) return "bg-yellow-300";
  if (score >= 8) return "bg-orange-400";
  if (score >= 6) return "bg-orange-300";
  return "bg-red-400";
};

const accumulateEmotionTimes = (currentTimes, data) => {
  // Make a copy of the current emotion times state
  const updatedTimes = { ...currentTimes };

  data.forEach((prediction) => {
    const duration = prediction.time.end - prediction.time.begin;
    prediction.emotions.forEach((emotion) => {
      const weightedTime = duration * emotion.score;
      if (!updatedTimes[emotion.name]) {
        updatedTimes[emotion.name] = 0;
      }
      updatedTimes[emotion.name] += weightedTime;
    });
  });

  return updatedTimes;
};

const onPredictionsReceived = (predictions, handleEmotionData) => {
  if (predictions.length === 0) {
    // console.log("No emotions detected");
  } else {
    // console.log("predictions:", predictions);
    handleEmotionData(predictions);
  }
};

function formatTitleCase(str) {
  return str
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function parseSimulationResult(assignment) {
  switch (assignment?.rolePlay?.type) {
    case "OBJECTIONS":
      return assignment?.objectionSimulationResult;
    case "PITCH":
      return assignment.pitchSimulationResult;
    case "DISCOVERY":
      return assignment?.discoverySimulationResult;
    case "PLAY":
      return assignment?.playSimulationResult;
    case "COLD_CALL":
      return assignment?.coldCallSimulationResult;
    case "MANAGEMENT":
      return assignment?.managementSimulationResult;
    default:
      return null;
  }
}

function getAssignmentTotalScore(assignment) {
  if (!assignment) return null;

  const primaryResult = parseSimulationResult(assignment);
  if (typeof primaryResult?.totalScore === "number") {
    return primaryResult.totalScore;
  }

  const possibleScores = [
    assignment?.objectionSimulationResult?.totalScore,
    assignment?.pitchSimulationResult?.totalScore,
    assignment?.discoverySimulationResult?.totalScore,
    assignment?.playSimulationResult?.totalScore,
    assignment?.coldCallSimulationResult?.totalScore,
    assignment?.managementSimulationResult?.totalScore,
  ];

  for (const maybeScore of possibleScores) {
    if (typeof maybeScore === "number") {
      return maybeScore;
    }
  }

  return null;
}

function assignmentMeetsPassingScore(assignment) {
  const passingScore = assignment?.rolePlay?.passingScore;
  if (typeof passingScore !== "number") {
    return false;
  }

  const totalScore = getAssignmentTotalScore(assignment);
  if (typeof totalScore !== "number") {
    return false;
  }

  return totalScore >= passingScore;
}

function formatOrdinal(value) {
  if (value === null || value === undefined) {
    return "";
  }

  const num = Number(value);
  if (!Number.isFinite(num)) {
    return `${value}`;
  }

  const abs = Math.abs(num);
  const mod100 = abs % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return `${num}th`;
  }

  const mod10 = abs % 10;
  switch (mod10) {
    case 1:
      return `${num}st`;
    case 2:
      return `${num}nd`;
    case 3:
      return `${num}rd`;
    default:
      return `${num}th`;
  }
}

// Helper function to clean up markdown formatting issues
const cleanMarkdown = (text) => {
  if (!text) return text;

  // Fix bold formatting with trailing spaces: **text ** -> **text**
  return text.replace(/\*\*([^*]+?)\s*\*\*/g, (match, content) => {
    return `**${content.trim()}**`;
  });
};

// Score highlighting utilities
const getScoreColorByValue = (score, maxScore = null) => {
  let percentage;

  if (maxScore) {
    percentage = (score / maxScore) * 100;
  } else {
    // Assume it's already a percentage if no maxScore provided
    percentage = score;
  }

  if (percentage >= 90)
    return {
      bg: "bg-green-100",
      text: "text-green-800",
      border: "border-green-200",
    };
  if (percentage >= 80)
    return {
      bg: "bg-yellow-100",
      text: "text-yellow-800",
      border: "border-yellow-200",
    };
  if (percentage >= 70)
    return {
      bg: "bg-orange-100",
      text: "text-orange-800",
      border: "border-orange-200",
    };
  if (percentage >= 60)
    return {
      bg: "bg-red-100",
      text: "text-red-800",
      border: "border-red-200",
    };
  return { bg: "bg-red-100", text: "text-red-800", border: "border-red-200" };
};

const processScoresInHTML = (html) => {
  if (!html) return html;

  // Simple regex-based approach to replace scores in HTML content
  let processedHtml = html;

  // Function to check if a match is inside an HTML tag
  const isInsideHtmlTag = (match, fullText, index) => {
    const beforeMatch = fullText.substring(0, index);
    const afterMatch = fullText.substring(index + match.length);

    // Count unclosed tags before the match
    const openTagsBefore = (beforeMatch.match(/</g) || []).length;
    const closeTagsBefore = (beforeMatch.match(/>/g) || []).length;

    // If we have more open tags than close tags, we might be inside a tag
    if (openTagsBefore > closeTagsBefore) {
      // Check if there's a closing > before any new opening <
      const nextCloseTag = afterMatch.indexOf(">");
      const nextOpenTag = afterMatch.indexOf("<");
      return (
        nextCloseTag !== -1 &&
        (nextOpenTag === -1 || nextCloseTag < nextOpenTag)
      );
    }

    return false;
  };

  // Function to check if text is already highlighted
  const isAlreadyHighlighted = (match, fullText, index) => {
    const beforeMatch = fullText.substring(Math.max(0, index - 300), index);
    const afterMatch = fullText.substring(
      index + match.length,
      index + match.length + 100
    );

    // Check if we're inside a score-highlight span or if the exact match is already highlighted
    const insideHighlight =
      beforeMatch.includes("score-highlight") &&
      !beforeMatch.includes("</span>");
    const afterHighlight =
      afterMatch.includes("</span>") && !afterMatch.includes("<span");
    const exactMatchAlreadyHighlighted =
      fullText.includes(`score-highlight" data-score`) &&
      fullText.includes(`>${match}</span>`);

    return insideHighlight || afterHighlight || exactMatchAlreadyHighlighted;
  };

  // Apply patterns in order of specificity (most specific first)

  // 1. First handle "Rating: X/Y" and "Score: X/Y" patterns
  processedHtml = processedHtml.replace(
    /(Rating|Score):\s*(\d+)\/(\d+)/gi,
    (match, label, score, maxScore, offset, string) => {
      if (
        isInsideHtmlTag(match, string, offset) ||
        isAlreadyHighlighted(match, string, offset)
      )
        return match;

      const scoreNum = parseInt(score);
      const maxScoreNum = parseInt(maxScore);
      const colors = getScoreColorByValue(scoreNum, maxScoreNum);
      return `<span class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium ${colors.bg} ${colors.text} ${colors.border} border mx-1 score-highlight" data-score="${scoreNum}" data-max-score="${maxScoreNum}">${label}: ${scoreNum}/${maxScoreNum}</span>`;
    }
  );

  // 1.5. Handle standalone "Rating: X" and "Score: X" patterns (without fractions)
  // First, let's specifically handle "Rating: 0" and "Score: 0" patterns
  processedHtml = processedHtml.replace(
    /(Rating|Score):\s*0(\s|$)/gi,
    (match, label, suffix, offset, string) => {
      if (
        isInsideHtmlTag(match, string, offset) ||
        isAlreadyHighlighted(match, string, offset)
      )
        return match;

      const colors = getScoreColorByValue(0, 10); // 0 out of 10 = 0% = red
      return `<span class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium ${colors.bg} ${colors.text} ${colors.border} border mx-1 score-highlight" data-score="0" data-max-score="10">${label}: 0</span>${suffix}`;
    }
  );

  // Then handle other standalone scores
  processedHtml = processedHtml.replace(
    /(Rating|Score):\s*([1-9]\d*)(?!\s*\/)/gi,
    (match, label, score, offset, string) => {
      if (
        isInsideHtmlTag(match, string, offset) ||
        isAlreadyHighlighted(match, string, offset)
      )
        return match;

      const scoreNum = parseInt(score);
      const maxScore = scoreNum <= 5 ? 5 : 10;
      const colors = getScoreColorByValue(scoreNum, maxScore);

      return `<span class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium ${colors.bg} ${colors.text} ${colors.border} border mx-1 score-highlight" data-score="${scoreNum}" data-max-score="${maxScore}">${label}: ${scoreNum}</span>`;
    }
  );

  // 2. Handle "Overall Score ... Score: X/Y" patterns
  processedHtml = processedHtml.replace(
    /(Overall Score[\s\n]*Score:\s*)(\d+)\/(\d+)/gi,
    (match, prefix, score, maxScore, offset, string) => {
      if (
        isInsideHtmlTag(match, string, offset) ||
        isAlreadyHighlighted(match, string, offset)
      )
        return match;

      const scoreNum = parseInt(score);
      const maxScoreNum = parseInt(maxScore);
      const colors = getScoreColorByValue(scoreNum, maxScoreNum);
      return `${prefix}<span class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium ${colors.bg} ${colors.text} ${colors.border} border mx-1 score-highlight" data-score="${scoreNum}" data-max-score="${maxScoreNum}">${scoreNum}/${maxScoreNum}</span>`;
    }
  );

  // 3. Handle X/100 scores specifically
  processedHtml = processedHtml.replace(
    /(\d+)\/100/g,
    (match, score, offset, string) => {
      if (
        isInsideHtmlTag(match, string, offset) ||
        isAlreadyHighlighted(match, string, offset)
      )
        return match;

      const scoreNum = parseInt(score);
      const colors = getScoreColorByValue(scoreNum, 100);
      return `<span class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium ${colors.bg} ${colors.text} ${colors.border} border mx-1 score-highlight" data-score="${scoreNum}" data-max-score="100">${scoreNum}/100</span>`;
    }
  );

  // 4. Finally handle general fractions, but be more conservative
  processedHtml = processedHtml.replace(
    /(\d{1,2})\/(\d{1,2})/g,
    (match, score, maxScore, offset, string) => {
      if (
        isInsideHtmlTag(match, string, offset) ||
        isAlreadyHighlighted(match, string, offset)
      )
        return match;

      // Check context - don't match if it's part of a larger number
      const beforeChar = offset > 0 ? string[offset - 1] : "";
      const afterChar =
        offset + match.length < string.length
          ? string[offset + match.length]
          : "";

      if (/\d/.test(beforeChar) || /\d/.test(afterChar)) {
        return match; // Skip if part of larger number
      }

      const scoreNum = parseInt(score);
      const maxScoreNum = parseInt(maxScore);

      // Only highlight if it looks like a score (reasonable range)
      if (maxScoreNum <= 10 && scoreNum <= maxScoreNum && maxScoreNum > 1) {
        const colors = getScoreColorByValue(scoreNum, maxScoreNum);
        return `<span class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium ${colors.bg} ${colors.text} ${colors.border} border mx-1 score-highlight" data-score="${scoreNum}" data-max-score="${maxScoreNum}">${scoreNum}/${maxScoreNum}</span>`;
      }
      return match;
    }
  );

  return processedHtml;
};

const NEW_TAB_LINK_ATTRS = 'target="_blank" rel="noopener noreferrer"';

const escapeMarkdownAttribute = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const getCodeLinkHref = (value = "") => {
  const href = String(value).trim();
  if (/^\/(?!\/)/.test(href)) {
    const internalOrigin = "https://salesably.internal";

    try {
      const parsedUrl = new URL(href, internalOrigin);
      return parsedUrl.origin === internalOrigin ? href : null;
    } catch {
      return null;
    }
  }

  try {
    const parsedUrl = new URL(href);
    return ["http:", "https:"].includes(parsedUrl.protocol) ? href : null;
  } catch {
    return null;
  }
};

const createMarkdownRenderer = ({
  openLinksInNewTab = false,
  linkifyCodeUrls = false,
} = {}) => {
  if (!openLinksInNewTab && !linkifyCodeUrls) return undefined;

  const renderer = new marked.Renderer();
  const defaultLinkRenderer = renderer.link.bind(renderer);
  const defaultCodespanRenderer = renderer.codespan.bind(renderer);

  if (openLinksInNewTab) {
    renderer.link = (token) => {
      const html = defaultLinkRenderer(token);

      if (token?.href?.startsWith("#")) {
        return html;
      }

      return html.replace(/^<a\s/, `<a ${NEW_TAB_LINK_ATTRS} `);
    };
  }

  if (linkifyCodeUrls) {
    renderer.codespan = (token) => {
      const href = getCodeLinkHref(token?.text);
      if (!href) return defaultCodespanRenderer(token);

      const linkAttrs = openLinksInNewTab ? ` ${NEW_TAB_LINK_ATTRS}` : "";
      return `<a${linkAttrs} href="${escapeMarkdownAttribute(href)}">Open link</a>`;
    };
  }

  return renderer;
};

// Enhanced version of cleanMarkdown that also processes scores
const cleanAndHighlightMarkdown = (
  text,
  { openLinksInNewTab = false, linkifyCodeUrls = false } = {}
) => {
  if (!text) return text;

  // First clean the markdown
  const cleaned = cleanMarkdown(text);

  // Convert to HTML using marked
  const renderer = createMarkdownRenderer({
    openLinksInNewTab,
    linkifyCodeUrls,
  });
  const html = marked(cleaned, renderer ? { renderer } : undefined);

  // Process scores in the HTML
  return processScoresInHTML(html);
};

const getScenarioName = (type, scoreDetails) => {
  switch (type) {
    case "PLAYS":
    case "ON-THE-FLY":
      return scoreDetails.Play?.name;
    case "OBJECTIONS":
      return scoreDetails.CustomerObjection?.objection;
    case "DISCOVERY":
      return scoreDetails.Discovery?.trackName;
    case "PITCH":
      return scoreDetails.Pitch?.pitch;
    case "COLD-CALL":
      return scoreDetails.ColdCall?.name;
    case "MANAGEMENT":
      return scoreDetails.managementScenario?.name;
    default:
      return "Unknown Scenario";
  }
};

const isVideoFile = (url) => {
  if (!url) return false;

  // If it's a conversation recording, treat it as audio
  if (url.includes("conversation.webm")) return false;

  const urlLower = url.toLowerCase();
  // For .webm files, check if it's a video
  if (urlLower.endsWith("video.webm")) return true;

  // Check other video extensions
  const videoExtensions = [".mp4", ".mov", ".avi", ".m4v"];
  return videoExtensions.some((ext) => {
    const index = urlLower.lastIndexOf(ext);
    if (index === -1) return false;
    return (
      index === urlLower.length - ext.length ||
      urlLower[index + ext.length] === "?"
    );
  });
};

export {
  conversationFromChat,
  parseRating,
  parseRatingCategory,
  calculateAverageScoreAllObjections,
  calculateAverageScoreSelectedObjection,
  getScoreColor100,
  getScoreColor20,
  accumulateEmotionTimes,
  onPredictionsReceived,
  getEncouragement,
  formatTitleCase,
  getYouTubeVideoId,
  parseSimulationResult,
  cleanMarkdown,
  getScoreColorByValue,
  processScoresInHTML,
  cleanAndHighlightMarkdown,
  getScenarioName,
  isVideoFile,
  getAssignmentTotalScore,
  assignmentMeetsPassingScore,
  formatOrdinal,
};
